/**
 * A custom element that renders a mobile sticky "add to cart" bar.
 *
 * It reveals itself once the product's primary add-to-cart button has scrolled out of view,
 * and hides again once that button is back on screen. The bar itself contains an independent
 * `product-form-component`, so variant changes, cart errors and cart additions all flow through
 * the theme's existing product form logic instead of being reimplemented here.
 */
class StickyCart extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    const target = this.#getObserveTarget();

    // No reference button found (for example, an unusual block setup) — fail open and stay visible.
    if (!target) {
      this.classList.add('sticky-cart--visible');
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.classList.toggle('sticky-cart--visible', !entry.isIntersecting);
      },
      { threshold: 0 }
    );

    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /**
   * Finds the product's primary add-to-cart button so we know when it leaves the viewport.
   * @returns {Element | null}
   */
  #getObserveTarget() {
    const section = this.closest('.shopify-section');

    return section?.querySelector('.product-details .add-to-cart-button, .product-details .product-form-buttons') ?? null;
  }
}

if (!customElements.get('sticky-cart-component')) {
  customElements.define('sticky-cart-component', StickyCart);
}
